/**
 * Fit Scoring Engine
 * 
 * Implements priority and future plans weighting for system recommendations.
 * 
 * Scoring model:
 *   FitScore(option, survey) =
 *     BaseFeasibility × (
 *       0.60 × TechnicalFit
 *     + 0.25 × PriorityFit
 *     + 0.15 × HeatLossConfidence
 *     )
 * 
 * - BaseFeasibility:      0-1   (hard constraints based on survey data)
 * - TechnicalFit:         0-100 (existing engine score)
 * - PriorityFit:          0-100 (priority-based weighting from survey.upgradePriorities)
 * - HeatLossConfidence:   0-100 (data completeness — surveys without heat loss data score lower)
 * 
 * Context-Aware Engineering Logic:
 * - Load-based zoning thresholds (>18 kW favors S-Plan)
 * - Load-based primary sizing (>19 kW favors 28mm primaries)
 * - Architecture auto-correction (unvented removes gravity constraints)
 */

import type {
  SystemRecInput,
  RecommendationOption,
  PriorityKey,
  PlanKey,
  UpgradePriority,
  FuturePlan,
  DistributionInput,
} from './types';
import {
  LOAD_ZONING_THRESHOLD_KW,
  LOAD_PRIMARY_THRESHOLD_KW,
  THRESHOLD_EXPLANATIONS,
  CONDITIONAL_SCORE_ADJUSTMENTS,
} from './engineeringThresholds';
import { calculateConfidence, calculateWholeHouseHeatLoss } from '../domain/heatLoss/calculateHeatLoss';

/**
 * Weights for fit score components.
 *
 * Scoring model:
 *   overall = BaseFeasibility × (
 *     TECHNICAL        × technicalFit
 *   + PRIORITY         × priorityFit
 *   + HEAT_LOSS_CONF   × heatLossConfidence
 *
 * Future fit is still computed for diagnostics/context but does not directly
 * contribute to the weighted overall score.
 *   )
 *
 * Heat loss confidence makes incomplete surveys visibly weaker —
 * a survey with no heat loss data will score lower than one with
 * full room-by-room measurement.
 */
const FIT_WEIGHTS = {
  TECHNICAL: 0.6,
  PRIORITY: 0.25,
  FUTURE: 0,
  HEAT_LOSS_CONFIDENCE: 0.15,
} as const;

const TECHNICAL_COMPONENT_WEIGHTS: Record<string, number> = {
  circuit_match: 0.2,
  mains_adequacy: 0.2,
  dhw_delivery: 0.2,
  emitter_compat: 0.15,
  heat_loss_compat: 0.15,
  standing_loss: 0.1,
};

/**
 * Rank weights for priorities
 * Rank 1 = highest importance (1.0)
 * Rank 2 = medium importance (0.7)
 * Rank 3 = lower importance (0.4)
 */
const RANK_WEIGHTS = {
  1: 1.0,
  2: 0.7,
  3: 0.4,
} as const;

/**
 * Horizon weights for future plans
 * 0-2 years = immediate consideration (1.0)
 * 2-5 years = medium term (0.7)
 * 5-10 years = long term (0.4)
 * unknown = moderate consideration (0.5)
 */
const HORIZON_WEIGHTS = {
  "0_2": 1.0,
  "2_5": 0.7,
  "5_10": 0.4,
  "unknown": 0.5,
} as const;

function getHorizonWeight(horizon: string | undefined): number {
  if (!horizon) {
    return HORIZON_WEIGHTS.unknown;
  }

  return HORIZON_WEIGHTS[horizon as keyof typeof HORIZON_WEIGHTS] ?? HORIZON_WEIGHTS.unknown;
}


function isHeatPumpModeOption(option: RecommendationOption): boolean {
  return option.systemType === 'heat_pump' || option.systemType === 'hybrid';
}

function isOnDemandOption(option: RecommendationOption): boolean {
  if (option.systemType === 'combi') return true;
  const water = (option.water || '').toLowerCase();
  const waterLabel = (option.waterLabel || '').toLowerCase();
  return water === 'on_demand' || waterLabel.includes('on demand');
}

function isHighDhwDemandContext(survey: SystemRecInput): boolean {
  return survey.hotWaterDemand === 'high' || (survey.occupancy ?? 0) >= 4 || (survey.drawsPerDay ?? 0) >= 7;
}

/**
 * Check if the heat source is a traditional boiler (gas/oil/lpg)
 * Used for determining circulation defaults and scoring logic
 */
function isTraditionalBoilerHeatSource(heatSource: string | undefined): boolean {
  return heatSource === 'gas' || heatSource === 'oil' || heatSource === 'lpg';
}

function isStoredToOnDemandConversion(option: RecommendationOption, survey: SystemRecInput): boolean {
  const currentWater = (survey.currentWater || '').toLowerCase();
  const storedWaterStates = new Set(['open_vented', 'vented', 'unvented', 'stored']);
  return storedWaterStates.has(currentWater) && isOnDemandOption(option);
}

function optionRequiresStoredDhw(option: RecommendationOption): boolean {
  if (option.systemType === 'combi') return false;
  const water = (option.water || '').toLowerCase();
  const waterLabel = (option.waterLabel || '').toLowerCase();
  if (water === 'on_demand' || water.includes('combi')) return false;
  if (waterLabel.includes('on demand') || waterLabel.includes('combi')) return false;
  return true;
}

function currentSystemTypeFromSurvey(survey: SystemRecInput): string | undefined {
  return (survey.currentBoiler || survey.currentSystem || survey.existingSystem || '').toString().toLowerCase() || undefined;
}

function currentWaterModeFromSurvey(survey: SystemRecInput): 'stored' | 'on_demand' | undefined {
  const currentWater = (survey.currentWater || '').toLowerCase();
  if (!currentWater) return undefined;
  if (currentWater === 'on_demand' || currentWater.includes('combi')) return 'on_demand';
  return 'stored';
}

function optionWaterMode(option: RecommendationOption): 'stored' | 'on_demand' {
  return optionRequiresStoredDhw(option) ? 'stored' : 'on_demand';
}

function isArchitectureChange(option: RecommendationOption, survey: SystemRecInput): boolean {
  const currentSystem = currentSystemTypeFromSurvey(survey);
  const proposedSystem = (option.systemType || option.boiler || '').toLowerCase();
  const currentWaterMode = currentWaterModeFromSurvey(survey);
  const proposedWaterMode = optionWaterMode(option);

  const boilerChanged = Boolean(currentSystem && proposedSystem && currentSystem !== proposedSystem);
  const waterModeChanged = Boolean(currentWaterMode && currentWaterMode !== proposedWaterMode);
  return boilerChanged || waterModeChanged;
}

function computePriorityInfluenceFactor(technicalFit: number, feasibility: number): number {
  const technicalFactor = Math.max(0, Math.min(1, (technicalFit - 40) / 45));
  return Math.max(0, Math.min(1, technicalFactor * feasibility));
}

/**
 * Compute heat loss data completeness confidence (0–100) from the survey.
 *
 * This feeds into the scoring formula as HEAT_LOSS_CONFIDENCE weight,
 * making surveys with incomplete heat loss data visibly weaker.
 *
 * Scoring tiers:
 *   100 — Full room-by-room data with U-values (from domain engine)
 *    75 — Known/measured heat loss provided (knownHeatLossKw)
 *    60 — Legacy heat loss field (heatLossKw)
 *    40 — Inferable from property type + age band
 *    25 — Property type only (no age band)
 *     0 — No heat loss data at all
 */
function computeHeatLossConfidence(survey: SystemRecInput): number {
  // Full room-level geometry and fabric data — use domain engine
  if (survey.heatLossRooms && survey.heatLossRooms.length > 0) {
    return calculateConfidence(survey.heatLossRooms);
  }
  // Canonical heatDemand.annualSpaceHeatDemandKwh — highest-confidence known field
  if (survey.heatDemand?.annualSpaceHeatDemandKwh && survey.heatDemand.annualSpaceHeatDemandKwh > 0) return 85;
  // Canonical heatDemand.designHeatLossKw
  if (survey.heatDemand?.designHeatLossKw && survey.heatDemand.designHeatLossKw > 0) return 80;
  // Measured or surveyed heat loss (legacy field)
  if (survey.knownHeatLossKw && survey.knownHeatLossKw > 0) return 75;
  // Legacy heat loss input
  if (survey.heatLossKw && survey.heatLossKw > 0) return 60;
  // Inferrable from property characteristics
  if (survey.propertyType && survey.propertyAgeBand) return 40;
  if (survey.propertyType) return 25;
  return 0;
}

/**
 * Fit score result with breakdown
 */
export interface FitScoreResult {
  overall: number;          // Overall fit score 0-100
  feasibility: number;      // BaseFeasibility 0-1
  technicalFit: number;     // Technical fit score 0-100
  priorityFit: number;      // Priority-based fit score 0-100
  futureFit: number | null; // Future plans fit score 0-100 (null when not assessed)
  futureFitAssessed: boolean;
  scoreLabel?: string;
  retrofitScore?: number;
  redesignedScore?: number;
  architectureLevel?: 'compatible' | 'moderate_upgrade' | 'full_redesign';
  redesignRequirements?: string[];
  /** Heat loss data completeness score (0–100). Drives scoring weight when available. */
  heatLossConfidence: number;
  breakdown: {
    technicalSubScores: Record<string, number>;  // Per-dimension technical scores
    priorityScores: Record<string, number | null>;    // Per-priority scores (null = not assessed)
    futurePlanScores: Record<string, number>;  // Per-plan scores
    futurePlanContexts?: Record<string, string>; // Context for each future plan score
    confidenceAdjustments: string[];           // Confidence-reducing factors
    reasons: string[];                         // Human-readable reasons
    killSwitchReasons?: string[];              // Fatal flaw reasons for 0 scores
    penalties?: Array<{ dimension: string; penalty: number; reason: string }>; // Explicit penalty breakdown
    constraintReasons?: ConstraintReason[];
    remediations?: Remediation[];              // Recommended upgrades to improve this option
    technicalAggregation?: {
      formula: string;
      weightsByComponent: Record<string, number>;
      preGateScore: number;
      gateMultiplier: number;
      gateReasons: string[];
      preClampScore: number;
      postClampScore: number;
      clampReasons: string[];
    };
    retrofitScore?: number;
    redesignedScore?: number;
    architectureLevel?: 'compatible' | 'moderate_upgrade' | 'full_redesign';
    redesignRequirements?: string[];
    scoreLabel?: string;
  };
}

export function getScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent Fit';
  if (score >= 75) return 'Strong Fit';
  if (score >= 60) return 'Acceptable with Trade-offs';
  return 'Significant Compromises';
}

export interface ConstraintReason {
  id: string;
  kind: 'hard' | 'soft';
  domain: 'technical' | 'preferences' | 'future';
  label: string;
  details?: string;
  sourcePath?: string;
}

export interface Remediation {
  id: string;
  category: 'cylinder' | 'emitters' | 'controls' | 'fabric' | 'mains';
  label: string;
  description: string;
  impact: string; // What improvement this provides
  costImpact?: 'low' | 'medium' | 'high';
}

/**
 * Compute fit score for an option
 */
export function computeOptionFitScore(
  option: RecommendationOption,
  survey: SystemRecInput
): FitScoreResult {
  // Extract priorities and future plans
  const priorities = survey.upgradePriorities || [];
  const futurePlans = survey.futurePlans || [];

  // Apply conditional load-based adjustments
  const { scoreAdjustment: conditionalAdjustment, explanations: conditionalExplanations } = 
    applyConditionalLoadAdjustments(option, survey);

  // Compute components - all normalized to 0-100 scale
  const feasibility = computeFeasibility(option, survey);

  const initialTechnicalSubScores = computeTechnicalSubScores(option, survey);
  const initialTechnicalAggregation = aggregateTechnicalFit(initialTechnicalSubScores, conditionalAdjustment);
  const initialTechnicalFit = initialTechnicalAggregation.postClampScore;
  
  const priorityFitRaw = computePriorityFit(option, priorities, survey);
  const priorityFit = Math.max(0, Math.min(100, priorityFitRaw));
  const futureFitRaw = computeFutureFit(option, futurePlans, survey);
  const futureFit = futureFitRaw === null ? null : Math.max(0, Math.min(100, futureFitRaw));
  const futureFitAssessed = futureFit !== null;
  const futureWeight = futureFitAssessed ? FIT_WEIGHTS.FUTURE : 0;

  // Compute weighted overall score (0-100 scale)
  // Detect kill switches and penalties
  const killSwitchReasons = detectKillSwitches(option, survey, initialTechnicalFit, feasibility);
  const penalties = computePenalties(option, survey, feasibility);
  const constraintReasons = buildConstraintReasons(option, survey, penalties, killSwitchReasons);
  
  // Collect breakdown details
  // If there are kill switches or severe feasibility issues, sync sub-scores with the impact
  const hasFatalFlaw = killSwitchReasons.length > 0 || feasibility < 0.3 || initialTechnicalFit < 10;
  const technicalSubScores = computeTechnicalSubScores(option, survey, hasFatalFlaw, feasibility, initialTechnicalFit);
  const technicalAggregation = aggregateTechnicalFit(technicalSubScores, conditionalAdjustment);
  const technicalFit = technicalAggregation.postClampScore;

  const priorityScores: Record<string, number | null> = {};
  priorities.forEach(p => {
    priorityScores[p.key] = scorePriority(option, p.key, survey);
  });

  const futurePlanScores: Record<string, number> = {};
  const futurePlanContexts: Record<string, string> = {};
  futurePlans.forEach(p => {
    const score = scoreFuturePlan(option, p.key, survey);
    futurePlanScores[p.key] = score;
    const context = getFuturePlanContext(option, p.key, score);
    if (context) {
      futurePlanContexts[p.key] = context;
    }
  });

  const confidenceAdjustments = computeConfidenceAdjustments(option, priorities, survey);
  const reasons = generateReasons(option, priorities, futurePlans, survey, conditionalExplanations);
  const remediations = detectRemediations(option, survey, technicalSubScores);

  const heatLossConfidence = computeHeatLossConfidence(survey);

  const priorityInfluenceFactor = computePriorityInfluenceFactor(technicalFit, feasibility);
  const effectivePriorityFit = 50 + (priorityFit - 50) * priorityInfluenceFactor;
  const effectiveFutureFit = futureFit === null ? null : 50 + (futureFit - 50) * priorityInfluenceFactor;

  // When heat loss confidence is below 60 (moderate), add a sizing confidence warning.
  // Use a stable prefix to avoid duplicating messages that already mention heat loss.
  const SIZING_CONFIDENCE_MESSAGE = 'Sizing confidence moderate — verify before final specification.';
  if (heatLossConfidence < 60 && !confidenceAdjustments.includes(SIZING_CONFIDENCE_MESSAGE)) {
    confidenceAdjustments.push(SIZING_CONFIDENCE_MESSAGE);
  }

  const weightedScore =
    FIT_WEIGHTS.TECHNICAL * technicalFit +
    FIT_WEIGHTS.PRIORITY * effectivePriorityFit +
    futureWeight * (effectiveFutureFit ?? 0) +
    FIT_WEIGHTS.HEAT_LOSS_CONFIDENCE * heatLossConfidence;

  if (priorityInfluenceFactor < 1 && (priorities.length > 0 || futureFitAssessed)) {
    reasons.push('Context physics currently dominates ranking; preference weighting is damped until technical fit constraints are resolved.');
  }

  const overall = Math.max(0, Math.min(100, feasibility * weightedScore));
  const scoreLabel = getScoreLabel(overall);
  const heatPumpArchitecture = option.systemType === 'heat_pump'
    ? computeHeatPumpArchitectureScoring(technicalSubScores, survey)
    : null;

  return {
    overall,
    feasibility,
    technicalFit,
    priorityFit,
    futureFit,
    futureFitAssessed,
    scoreLabel,
    retrofitScore: heatPumpArchitecture?.retrofitScore,
    redesignedScore: heatPumpArchitecture?.redesignedScore,
    architectureLevel: heatPumpArchitecture?.architectureLevel,
    redesignRequirements: heatPumpArchitecture?.redesignRequirements,
    heatLossConfidence,
    breakdown: {
      technicalSubScores,
      priorityScores,
      futurePlanScores,
      futurePlanContexts: Object.keys(futurePlanContexts).length > 0 ? futurePlanContexts : undefined,
      confidenceAdjustments,
      reasons,
      killSwitchReasons: killSwitchReasons.length > 0 ? killSwitchReasons : undefined,
      penalties: penalties.length > 0 ? penalties : undefined,
      constraintReasons: constraintReasons.length > 0 ? constraintReasons : undefined,
      remediations: remediations.length > 0 ? remediations : undefined,
      technicalAggregation,
      scoreLabel,
      retrofitScore: heatPumpArchitecture?.retrofitScore,
      redesignedScore: heatPumpArchitecture?.redesignedScore,
      architectureLevel: heatPumpArchitecture?.architectureLevel,
      redesignRequirements: heatPumpArchitecture?.redesignRequirements,
    },
  };
}

function normalizeTechnicalScore(score: number): number {
  if (!Number.isFinite(score)) return 0;
  // Backward compatible: older pipelines may still emit 0..10 diagnostics.
  if (score <= 10) return score * 10;
  return score;
}

function aggregateTechnicalFit(
  technicalSubScores: Record<string, number>,
  conditionalAdjustment: number
): {
  formula: string;
  weightsByComponent: Record<string, number>;
  preGateScore: number;
  gateMultiplier: number;
  gateReasons: string[];
  preClampScore: number;
  postClampScore: number;
  clampReasons: string[];
} {
  const keys = Object.keys(technicalSubScores);
  const weightedComponents = keys
    .map((key) => {
      const raw = technicalSubScores[key];
      const score = normalizeTechnicalScore(raw);
      const weight = TECHNICAL_COMPONENT_WEIGHTS[key] ?? 0;
      return { key, score, weight };
    })
    .filter((component) => component.weight > 0);

  const totalWeight = weightedComponents.reduce((sum, component) => sum + component.weight, 0);
  const normalisedWeights = Object.fromEntries(
    weightedComponents.map((component) => [component.key, totalWeight > 0 ? component.weight / totalWeight : 0])
  );

  const baseTechnicalFit = totalWeight > 0
    ? weightedComponents.reduce((sum, component) => sum + (component.score * component.weight), 0) / totalWeight
    : 50;

  const preClampScore = baseTechnicalFit + conditionalAdjustment;
  const postClampScore = Math.max(0, Math.min(100, preClampScore));
  const clampReasons: string[] = [];
  if (preClampScore < 0) {
    clampReasons.push('technical fit clamped to lower bound 0');
  }
  if (preClampScore > 100) {
    clampReasons.push('technical fit clamped to upper bound 100');
  }

  return {
    formula: 'technicalFit = clamp(weightedAverage(technicalSubScoresScaledTo100) + conditionalLoadAdjustment, 0, 100)',
    weightsByComponent: normalisedWeights,
    preGateScore: baseTechnicalFit,
    gateMultiplier: 1,
    gateReasons: ['technical sub-scores are weighted directly in fitScoring'],
    preClampScore,
    postClampScore,
    clampReasons,
  };
}

/**
 * Compute base feasibility (hard constraints)
 * Returns 0-1 scale
 */
function computeFeasibility(
  option: RecommendationOption,
  survey: SystemRecInput
): number {
  let feasibility = 1.0;

  // Penalty for missing critical inputs
  feasibility -= penaltyMissingCriticalInputs(option, survey);

  // Penalty for known incompatibilities
  feasibility -= penaltyKnownIncompatibility(option, survey);

  // Clamp to 0-1 range
  return Math.max(0, Math.min(1, feasibility));
}

/**
 * Penalty for missing critical inputs
 */
function penaltyMissingCriticalInputs(
  option: RecommendationOption,
  survey: SystemRecInput
): number {
  let penalty = 0;

  // Check for missing mains flow/pressure for unvented systems
  if (option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented')) {
    if (!survey.mainsFlowLpm && !survey.flowRate) {
      penalty += 0.2;
    }
    if (!survey.mainsStaticPressureBar && !survey.mainsPressure) {
      penalty += 0.2;
    }
  }

  // Check for missing heat loss for heat pumps
  if (isHeatPumpModeOption(option)) {
    const hasHeatLoss =
      survey.heatLossKw ||
      survey.knownHeatLossKw ||
      survey.heatDemand?.designHeatLossKw ||
      survey.heatDemand?.annualSpaceHeatDemandKwh;
    if (!hasHeatLoss) {
      penalty += 0.15;
    }
  }

  return penalty;
}

/**
 * Penalty for known incompatibilities
 */
function penaltyKnownIncompatibility(
  option: RecommendationOption,
  survey: SystemRecInput
): number {
  let penalty = 0;

  // Heat pump with high-temp emitters
  if (isHeatPumpModeOption(option)) {
    const lowTempSuitability = survey.emitters?.lowTempSuitability;
    if (lowTempSuitability === 'definitely_needs_upgrade') {
      penalty += 0.3;
    } else if (lowTempSuitability === 'likely_needs_upgrade') {
      penalty += 0.15;
    }
  }

  // Gas system without gas availability
  if ((option.systemType === 'combi' || option.systemType === 'system' || option.systemType === 'regular') 
      && survey.gasAvailable === false) {
    penalty += 0.5;
  }

  // Stored-DHW options are not viable when cylinder space is explicitly unavailable
  if (survey.cylinderSpace === false && optionRequiresStoredDhw(option)) {
    penalty += 0.8;
  }

  return penalty;
}

/**
 * Compute priority fit score (0-100)
 * Weighted average of priority scores
 */
function computePriorityFit(
  option: RecommendationOption,
  priorities: UpgradePriority[],
  survey: SystemRecInput
): number {
  if (priorities.length === 0) {
    return 100; // No priorities = full score (neutral)
  }

  let weightedSum = 0;
  let totalWeight = 0;

  for (const priority of priorities) {
    const weight = RANK_WEIGHTS[priority.rank];
    const score = scorePriority(option, priority.key, survey);
    if (score === null) {
      continue;
    }
    weightedSum += weight * score;
    totalWeight += weight;
  }

  return totalWeight > 0 ? weightedSum / totalWeight : 100;
}

/**
 * Compute future fit score (0-100)
 * Weighted average of future plan impacts
 */
function computeFutureFit(
  option: RecommendationOption,
  futurePlans: FuturePlan[],
  survey: SystemRecInput
): number | null {
  if (futurePlans.length === 0) {
    return null;
  }

  let weightedSum = 0;
  let totalWeight = 0;

  for (const plan of futurePlans) {
    // Skip "no_plans" and "unsure" from scoring
    if (plan.key === 'no_plans' || plan.key === 'unsure') {
      continue;
    }

    const weight = getHorizonWeight(plan.horizon);
    const score = scoreFuturePlan(option, plan.key, survey);
    weightedSum += weight * score;
    totalWeight += weight;
  }

  return totalWeight > 0 ? weightedSum / totalWeight : null;
}

function buildConstraintReasons(
  option: RecommendationOption,
  survey: SystemRecInput,
  penalties: Array<{ dimension: string; penalty: number; reason: string }>,
  killSwitchReasons: string[]
): ConstraintReason[] {
  const reasons: ConstraintReason[] = [];

  const hasMinimalDisruption = (survey.upgradePriorities || []).some((p) => p.key === 'minimal_disruption');
  if (hasMinimalDisruption && (option.systemType === 'heat_pump' || option.systemType === 'hybrid')) {
    reasons.push({
      id: 'brief.disruption.minimal',
      kind: 'hard',
      domain: 'preferences',
      label: '“Minimise disruption” selected',
      details: 'This option typically requires wider system changes and pipework adjustments.',
      sourcePath: 'hub://priorities',
    });
  }

  if (survey.cylinderSpace === false && optionRequiresStoredDhw(option)) {
    reasons.push({
      id: 'no_cylinder_space',
      kind: 'hard',
      domain: 'technical',
      label: 'No space for a hot-water cylinder',
      details: 'Stored-DHW options require cylinder space, but input says no cylinder space is available.',
      sourcePath: 'hub://system-details.cylinderSpace',
    });
  }

  for (const killSwitch of killSwitchReasons) {
    reasons.push({
      id: `technical.kill_switch.${reasons.length + 1}`,
      kind: 'hard',
      domain: 'technical',
      label: killSwitch.replace('Incompatible: ', '').replace('Not suitable: ', ''),
      details: killSwitch,
      sourcePath: 'hub://dhw-pressure',
    });
  }

  for (const penalty of penalties) {
    reasons.push({
      id: `penalty.${penalty.dimension.toLowerCase().replace(/\s+/g, '_')}`,
      kind: 'soft',
      domain: 'technical',
      label: penalty.dimension,
      details: penalty.reason,
      sourcePath: 'hub://priorities',
    });
  }

  if (isOnDemandOption(option) && isHighDhwDemandContext(survey)) {
    reasons.push({
      id: 'dhw_peak_risk',
      kind: 'soft',
      domain: 'technical',
      label: 'Peak DHW demand risk for on-demand hot water',
      details: 'Stored hot water usually provides better comfort for multiple consecutive draws in higher-demand homes.',
      sourcePath: 'hub://usage.hotWaterDemand',
    });
  }

  if (
    isStoredToOnDemandConversion(option, survey) &&
    survey.cylinderSpace === true &&
    isHighDhwDemandContext(survey)
  ) {
    reasons.push({
      id: 'service_downgrade',
      kind: 'soft',
      domain: 'preferences',
      label: 'Potential service downgrade from stored to on-demand hot water',
      details: 'Cylinder space is available and demand is high, so switching to on-demand may reduce peak hot-water comfort.',
      sourcePath: 'hub://system-details.cylinderSpace',
    });
  }

  return reasons;
}

/**
 * Detect remediations - upgrades that can improve this option
 * Standing loss issues should drive cylinder upgrade recommendations, not tank the option
 */
function detectRemediations(
  option: RecommendationOption,
  survey: SystemRecInput,
  technicalSubScores: Record<string, number>
): Remediation[] {
  const remediations: Remediation[] = [];

  // Standing loss remediation - recommend cylinder upgrade for poor standing loss
  const standingLossScore = technicalSubScores['standing_loss'];
  if (standingLossScore !== undefined && standingLossScore < 65) {
    const isVented = option.water === 'open_vented' || option.waterLabel?.toLowerCase().includes('open vented');
    const isMixergy = option.waterLabel?.toLowerCase().includes('mixergy') || option.title?.toLowerCase().includes('mixergy');
    
    // Only recommend upgrade if not already using Mixergy
    if (!isMixergy) {
      remediations.push({
        id: 'remediation.cylinder.standing_loss',
        category: 'cylinder',
        label: 'Upgrade to high-performance cylinder',
        description: isVented 
          ? 'Replace with modern well-insulated vented cylinder or consider Mixergy'
          : 'Replace with Mixergy or modern high-performance unvented cylinder',
        impact: 'Reduces standing heat loss from 2.0-2.5 kWh/day to 0.7-1.0 kWh/day',
        costImpact: 'medium',
      });
    }
  }

  // Emitter remediation for heat pumps with low emitter compatibility
  const emitterScore = technicalSubScores['emitter_compat'];
  if (option.systemType === 'heat_pump' && emitterScore !== undefined && emitterScore < 50) {
    const lowTempSuitability = survey.emitters?.lowTempSuitability;
    if (lowTempSuitability === 'likely_needs_upgrade' || lowTempSuitability === 'definitely_needs_upgrade') {
      remediations.push({
        id: 'remediation.emitters.low_temp',
        category: 'emitters',
        label: 'Upgrade radiators for low-temperature operation',
        description: 'Install larger radiators or underfloor heating to work efficiently at 45-50°C flow temperatures',
        impact: 'Enables efficient heat pump operation with SCOP 3.0-3.5+',
        costImpact: 'high',
      });
      remediations.push({
        id: 'remediation.emitters.complete_repipe',
        category: 'emitters',
        label: 'Complete repipe and low-temp readiness upgrade',
        description: 'Repipe primary and distribution circuits and rebalance emitters so the home can run at low flow temperatures.',
        impact: 'Makes heat pump operation viable where the current system is not low-temperature ready.',
        costImpact: 'high',
      });
    }
  }

  const primarySize = getPrimaryPipeSize(survey);
  if (primarySize === 15) {
    remediations.push({
      id: 'remediation.distribution.primary_repipe_15mm',
      category: 'emitters',
      label: 'Primary repipe required (15mm trunk)',
      description: '15mm primary circuits are undersized for modern system performance and should be repiped.',
      impact: 'Improves circulation, reduces velocity issues, and unlocks stable operation across boiler and heat-pump pathways.',
      costImpact: 'high',
    });
  }

  // Mains adequacy remediation
  const mainsScore = technicalSubScores['mains_adequacy'];
  if (mainsScore !== undefined && mainsScore < 50) {
    const flowRaw = survey.mainsFlowLpm ?? survey.flowRate;
    const pressureRaw = survey.mainsStaticPressureBar ?? survey.mainsPressure;
    const hasLowFlow = typeof flowRaw === 'number' && flowRaw < 12;
    const hasLowPressure = typeof pressureRaw === 'number' && pressureRaw < 1.5;
    
    if (hasLowFlow || hasLowPressure) {
      remediations.push({
        id: 'remediation.mains.upgrade',
        category: 'mains',
        label: 'Upgrade mains water supply',
        description: 'Install accumulator tank or upgrade supply pipe from street to improve pressure/flow',
        impact: hasLowPressure && hasLowFlow 
          ? 'Improves both pressure and flow rate for better performance'
          : hasLowPressure 
            ? 'Improves water pressure for consistent delivery'
            : 'Improves flow rate for simultaneous tap use',
        costImpact: 'high',
      });
    }
  }

  return remediations;
}

/**
 * Score how well an option matches a priority (0-100)
 */
function scorePriority(
  option: RecommendationOption,
  priorityKey: PriorityKey,
  survey: SystemRecInput
): number | null {
  switch (priorityKey) {
    case 'low_upfront_cost':
      return scoreLowUpfrontCost(option, survey);
    
    case 'low_running_cost':
      return scoreLowRunningCost(option, survey);
    
    case 'reliability_simplicity':
      return scoreReliabilitySimplicity(option, survey);
    
    case 'hot_water_performance':
      return scoreHotWaterPerformance(option, survey);
    
    case 'quiet_operation':
      return scoreQuietOperation(option, survey);
    
    case 'future_proofing':
      return scoreFutureProofing(option, survey);
    
    case 'minimal_disruption':
      return scoreMinimalDisruption(option, survey);
    
    case 'smart_controls':
      return scoreSmartControls(option, survey);
    
    case 'environmental_impact':
      return scoreEnvironmentalImpact(option, survey);
    
    case 'comfort_even_heat':
      return scoreComfortEvenHeat(option, survey);
    
    default:
      return 50; // Neutral for unknown priorities
  }
}

/**
 * Score future plan impact (0-100)
 * Returns contextual score indicating specific match to the plan
 */
function scoreFuturePlan(
  option: RecommendationOption,
  planKey: PlanKey,
  survey: SystemRecInput
): number {
  switch (planKey) {
    case 'loft_conversion':
    case 'rear_extension':
    case 'side_extension':
    case 'garage_conversion':
      return scoreCapacityGrowth(option, survey);
    
    case 'additional_bathroom':
    case 'cylinder_larger_future':
      return scoreDHWGrowth(option, survey);
    
    case 'ufh_future':
      return scoreUFHFuture(option, survey);
    
    case 'solar_pv':
    case 'battery_storage':
    case 'ev_charger':
      return scoreElectrification(option, survey);
    
    case 'no_plans':
    case 'unsure':
      return 100; // Neutral
    
    default:
      return 50; // Unknown plan
  }
}

/**
 * Get human-readable context for why a future plan score is given
 * This helps explain whether a 100 is truly "perfect fit" or just "doesn't conflict"
 */
function getFuturePlanContext(
  option: RecommendationOption,
  planKey: PlanKey,
  score: number
): string | undefined {
  // Only provide context for meaningful scores
  if (score === 100 && (planKey === 'no_plans' || planKey === 'unsure')) {
    return undefined; // Don't clutter with "no plans" explanations
  }

  switch (planKey) {
    case 'loft_conversion':
    case 'rear_extension':
    case 'side_extension':
    case 'garage_conversion':
      if (option.systemType === 'combi') {
        return score >= 60 ? 'Adequate but limited expansion capacity' : 'Poor fit for capacity growth';
      }
      if (option.systemType === 'heat_pump' || option.systemType === 'system') {
        return 'Excellent headroom for capacity expansion';
      }
      break;
    
    case 'additional_bathroom':
    case 'cylinder_larger_future':
      if (option.systemType === 'combi') {
        return 'Limited by mains flow rate - difficult to expand DHW';
      }
      if (option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented')) {
        return 'Cylinder-based design easily upgradeable';
      }
      break;
    
    case 'ufh_future':
      if (option.systemType === 'heat_pump') {
        return 'Ideal for low-temperature underfloor heating';
      }
      if (score < 70) {
        return 'May require upgrades for UFH compatibility';
      }
      break;
    
    case 'solar_pv':
    case 'battery_storage':
    case 'ev_charger':
      if (option.systemType === 'heat_pump') {
        return 'Excellent synergy with electrification plans';
      }
      if (score < 60) {
        return 'Limited synergy with electrification goals';
      }
      break;
  }
  
  return undefined;
}

// ============================================================================
// Architecture and Context Detection Helpers
// ============================================================================

/**
 * Detect if option uses unvented cylinder architecture
 */
function isUnventedArchitecture(option: RecommendationOption): boolean {
  return option.water === 'unvented' || 
         option.waterLabel?.toLowerCase().includes('unvented') || false;
}

/**
 * Detect if option uses open-vented cylinder architecture
 */
function isOpenVentedArchitecture(option: RecommendationOption): boolean {
  return option.water === 'open_vented' || 
         option.waterLabel?.toLowerCase().includes('open vented') || 
         (option.waterLabel?.toLowerCase().includes('vented') && 
          !option.waterLabel?.toLowerCase().includes('unvented')) || false;
}

/**
 * Detect if option uses Y-Plan distribution
 */
function isYPlanDistribution(option: RecommendationOption, survey: SystemRecInput): boolean {
  const dist = survey.distribution as DistributionInput | undefined;
  return option.waterLabel?.toLowerCase().includes('y-plan') || 
         (dist?.systemLayout === 'y_plan') || false;
}

/**
 * Detect if option uses S-Plan distribution
 */
function isSPlanDistribution(option: RecommendationOption, survey: SystemRecInput): boolean {
  const dist = survey.distribution as DistributionInput | undefined;
  return option.waterLabel?.toLowerCase().includes('s-plan') || 
         (dist?.systemLayout === 's_plan') || false;
}

/**
 * Get design heat load from survey
 */
function getDesignLoadKw(survey: SystemRecInput): number {
  return (
    survey.heatDemand?.designHeatLossKw ||
    survey.knownHeatLossKw ||
    survey.heatLossKw ||
    (survey.heatDemand?.annualSpaceHeatDemandKwh
      ? survey.heatDemand.annualSpaceHeatDemandKwh / 1900
      : 0)
  );
}

/**
 * Get primary pipe size from survey
 */
function getPrimaryPipeSize(survey: SystemRecInput): number | null {
  // Try distribution.primaryPipeSize first
  const dist = survey.distribution as DistributionInput | undefined;
  const size = dist?.primaryPipeSize || survey.primaryPipeSize;
  
  if (typeof size === 'string') {
    // Parse '28mm' -> 28
    const parsed = parseInt(size.replace('mm', ''), 10);
    return isNaN(parsed) ? null : parsed;
  }
  return typeof size === 'number' ? size : null;
}

// ============================================================================
// Technical Sub-Score Functions
// ============================================================================

function computeTechnicalSubScores(
  option: RecommendationOption,
  survey: SystemRecInput,
  hasFatalFlaw: boolean = false,
  feasibility: number = 1.0,
  technicalFit: number = 100
): Record<string, number> {
  const scores: Record<string, number> = {};

  // Circuit compatibility
  scores['circuit_match'] = scoreTechCircuitMatch(option, survey);

  // Mains adequacy
  scores['mains_adequacy'] = scoreTechMainsAdequacy(option, survey);

  // DHW delivery quality
  scores['dhw_delivery'] = scoreTechDhwDelivery(option, survey);

  // Emitter compatibility
  scores['emitter_compat'] = scoreTechEmitterCompat(option, survey);

  // Heat loss compatibility (only for heat pumps)
  if (option.systemType === 'heat_pump') {
    scores['heat_loss_compat'] = scoreTechHeatLossCompat(option, survey);
  }

  // Standing loss efficiency
  if (!isOnDemandOption(option)) {
    scores['standing_loss'] = scoreTechStandingLoss(option, survey);
  }

  // If there's a fatal flaw, sync sub-scores with overall technical fit
  // This prevents "ghost sub-scores" where individual components look good
  // but the system as a whole is unsuitable
  if (hasFatalFlaw || feasibility < 0.5 || technicalFit < 20) {
    // Apply feasibility penalty to all sub-scores proportionally
    // This shows that even if individual components are good, they're dragged down by the fatal flaw
    const penaltyFactor = Math.min(feasibility, technicalFit / 100);
    
    for (const key in scores) {
      // Apply penalty but keep some signal about which components are actually limiting
      // If a sub-score is already low (< 40), it's likely a primary constraint, so show it
      // If a sub-score is high (>= 40), scale it down to reflect the overall failure
      if (scores[key] >= 40) {
        scores[key] = Math.round(scores[key] * penaltyFactor);
      }
    }
  }

  return scores;
}

function computeHeatPumpArchitectureScoring(
  technicalSubScores: Record<string, number>,
  survey: SystemRecInput,
): {
  retrofitScore: number;
  redesignedScore: number;
  redesignRequirements: string[];
  architectureLevel: 'compatible' | 'moderate_upgrade' | 'full_redesign';
} {
  const emitterCompatibility = technicalSubScores['emitter_compat'] ?? 45;
  const circuitMatch = technicalSubScores['circuit_match'] ?? 65;
  const standingLoss = technicalSubScores['standing_loss'] ?? 60;
  const mainsAdequacy = technicalSubScores['mains_adequacy'] ?? 60;
  const heatLossCompat = technicalSubScores['heat_loss_compat'] ?? 50;
  const retrofitDhwDelivery = technicalSubScores['dhw_delivery'] ?? 55;

  const retrofitScore = Math.round((
    emitterCompatibility * 0.34 +
    circuitMatch * 0.24 +
    retrofitDhwDelivery * 0.22 +
    standingLoss * 0.10 +
    heatLossCompat * 0.10
  ));

  let architectureLevel: 'compatible' | 'moderate_upgrade' | 'full_redesign' = 'compatible';
  if (emitterCompatibility < 60) architectureLevel = 'full_redesign';
  else if (emitterCompatibility < 75) architectureLevel = 'moderate_upgrade';

  const redesignedEmitterCompatibility = 90;
  const redesignedDhwDelivery = mainsAdequacy >= 70 ? 90 : 75;

  let redesignedScore = Math.round((
    redesignedEmitterCompatibility * 0.34 +
    circuitMatch * 0.24 +
    redesignedDhwDelivery * 0.22 +
    standingLoss * 0.10 +
    heatLossCompat * 0.10
  ));

  const designHeatLossKw = getDesignLoadKw(survey);
  if (designHeatLossKw > 0 && designHeatLossKw < 18) redesignedScore += 5;
  if (survey.insulationLevel === 'high' || survey.insulationLevel === 'medium') redesignedScore += 3;
  if (mainsAdequacy >= 70) redesignedScore += 3;
  redesignedScore = Math.max(0, Math.min(100, redesignedScore));

  const redesignRequirements = [
    'Radiator upgrades sized for ≤45°C flow',
    'Weather compensation control',
    'Full room-by-room heat loss',
    'Correctly matched cylinder',
    'Hydraulic optimisation where required',
  ];

  return {
    retrofitScore,
    redesignedScore,
    redesignRequirements,
    architectureLevel,
  };
}

function scoreTechCircuitMatch(option: RecommendationOption, survey: SystemRecInput): number {
  const currentCircuit = survey.circuitType || survey.heatingCircuit || survey.chCircuitType;
  if (!currentCircuit) return 65; // unknown

  const isSealed = currentCircuit === 'sealed' || currentCircuit === 'pressurised';
  const isOpenVented = currentCircuit === 'open-vented' || currentCircuit === 'open_vented' || currentCircuit === 'gravity';
  const chCirculation = survey.chCirculation || 'unknown';
  const designLoadKw = getDesignLoadKw(survey);
  const primarySizeMm = getPrimaryPipeSize(survey);
  const emitterSuitability = survey.emitters?.lowTempSuitability;
  const usesYPlan = isYPlanDistribution(option, survey);
  const proposedSystemType = option.systemType || ((option.boiler === 'air_source_hp' || option.boiler === 'ground_source_hp')
    ? 'heat_pump'
    : option.boiler === 'system'
      ? 'system'
      : option.boiler === 'regular'
        ? 'regular'
        : option.boiler === 'combi' || option.boiler === 'storage_combi'
          ? 'combi'
          : 'unknown');

  // Check if this is a traditional boiler system (gas/oil/lpg, not heat pump)
  const heatSource = survey.heatSource || 'unknown';
  const isTraditionalBoilerSystem = isTraditionalBoilerHeatSource(heatSource);

  // Like-for-like regular/open-vented gas should be treated as a strong match.
  if (proposedSystemType === 'regular') {
    if (isOpenVented) return 94;
    if (isSealed) return 82;
    return 78;
  }

  // Gas boiler pathways are fully compatible with 22mm primaries for <=19kW loads
  // BUT only if they are sealed OR open-vented with confirmed pumped circulation
  const isGasPathway = proposedSystemType === 'combi' || proposedSystemType === 'system' || proposedSystemType === 'regular';
  const hasCompatibleCirculation = isSealed || (isOpenVented && chCirculation === 'pumped');
  if (isGasPathway && hasCompatibleCirculation && primarySizeMm === 22 && designLoadKw > 0 && designLoadKw <= LOAD_PRIMARY_THRESHOLD_KW) {
    return 100;
  }

  // Open-vented pumped CH is fully valid for traditional boiler systems (gas/oil/lpg)
  // This addresses the issue where open-vented systems were penalized even when pumped
  if (isTraditionalBoilerSystem && isOpenVented && chCirculation === 'pumped') {
    // For system or combi boilers with open-vented pumped CH, treat as highly compatible
    if (proposedSystemType === 'system' || proposedSystemType === 'combi') {
      return 90; // High compatibility for pumped open-vented systems
    }
  }

  let score = 88;

  // If circulation is unknown for open-vented systems, apply conservative score
  // This is the main penalty for not knowing if it's pumped or gravity
  // Return directly since 65 already reflects the uncertainty and is within valid range
  if (isOpenVented && chCirculation === 'unknown') {
    return 65;
  }

  // Conversion work from open-vented to sealed architectures should be a moderate
  // compatibility drag (not a hard mismatch) - but only for non-pumped circulation
  const optionIsUnvented = isUnventedArchitecture(option);
  const optionIsOpenVented = isOpenVentedArchitecture(option);
  
  // Only apply conversion penalty if:
  // 1. Current system is open-vented
  // 2. Option is sealed (unvented or combi without cylinder)
  // 3. Circulation is not explicitly pumped
  const isConvertingToSealed = isOpenVented && optionIsUnvented;
  if ((proposedSystemType === 'system' || proposedSystemType === 'combi') && isConvertingToSealed) {
    if (chCirculation !== 'pumped') {
      score -= 14;
    }
  }

  // Stored-DHW -> combi conversions need more hydraulic rework.
  const hasStoredDhw = survey.currentWaterType === 'open_vented' ||
    survey.currentWaterType === 'unvented' ||
    survey.currentWaterType === 'vented_cylinder' ||
    survey.currentWaterType === 'thermal_store';
  if (proposedSystemType === 'combi' && hasStoredDhw) {
    score -= 8;
  }

  // Low-temperature suitability should only impact heat-pump options.
  if (proposedSystemType === 'heat_pump') {
    score = 72;

    if (emitterSuitability === 'likely_needs_upgrade') score -= 12;
    if (emitterSuitability === 'definitely_needs_upgrade') score -= 22;

    // Y-plan only becomes a circuit drag for heat-pump pathways.
    if (usesYPlan) score -= 6;

    // High load on marginal primary pipework can require hydraulic separation.
    if (designLoadKw > LOAD_ZONING_THRESHOLD_KW && primarySizeMm !== null && primarySizeMm <= 22) {
      score -= 8;
    }
  } else if (designLoadKw > LOAD_PRIMARY_THRESHOLD_KW && primarySizeMm !== null && primarySizeMm <= 22) {
    // Mild non-HP drag at high load where 22 mm primaries may constrain circulation.
    score -= 4;
  }

  return Math.max(45, Math.min(95, score));
}

function scoreTechMainsAdequacy(option: RecommendationOption, survey: SystemRecInput): number {
  const flow = survey.mainsFlowLpm || survey.flowRate || 0;
  const pressure = survey.mainsStaticPressureBar || survey.mainsPressure || 0;
  const hasMeasurement = flow > 0 || pressure > 0;

  // Options that don't need mains pressure
  const isVented = option.water === 'open_vented' || option.waterLabel?.toLowerCase().includes('open vented');
  if (isVented) return 90;

  if (!hasMeasurement) return 30; // not assessed

  const isUnvented = option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented');
  const isCombi = option.systemType === 'combi';

  if (isUnvented) {
    // Unvented needs flow >= 16 lpm and pressure >= 1.5 bar
    if (flow >= 16 && pressure >= 1.5) return 95;
    if (flow >= 12 && pressure >= 1.0) return 60;
    return 20;
  }
  if (isCombi) {
    // Combi needs flow >= 12 lpm and pressure >= 1.0 bar
    if (flow >= 12 && pressure >= 1.0) return 90;
    if (flow >= 8 && pressure >= 0.5) return 55;
    return 15;
  }
  return 70;
}

function scoreTechDhwDelivery(option: RecommendationOption, survey: SystemRecInput): number {
  const isUnvented = isUnventedArchitecture(option);
  const isVented = isOpenVentedArchitecture(option);
  const isCombi = option.systemType === 'combi';
  const isMixergy = option.waterLabel?.toLowerCase().includes('mixergy') || option.title?.toLowerCase().includes('mixergy');
  const dhwDeliveryMethod = survey.dhwDeliveryMethod ?? 'unknown';

  // Architecture auto-correction: Unvented improves DHW delivery baseline
  if (isUnvented) {
    const flow = survey.mainsFlowLpm || survey.flowRate || 0;
    const pressure = survey.mainsStaticPressureBar || survey.mainsPressure || 0;
    
    // Apply baseline boost for unvented architecture
    if (flow >= 16 && pressure >= 1.5) {
      const mixergyBonus = isMixergy ? CONDITIONAL_SCORE_ADJUSTMENTS.MIXERGY_UNVENTED_DHW_BONUS : 0;
      return 95 + mixergyBonus;
    }
    if (flow > 0) return 60 + CONDITIONAL_SCORE_ADJUSTMENTS.UNVENTED_DHW_BASELINE_BOOST;
    return 40 + CONDITIONAL_SCORE_ADJUSTMENTS.UNVENTED_DHW_BASELINE_BOOST;
  }
  
  if (isVented) {
    if (dhwDeliveryMethod === 'pumped') {
      return isMixergy ? 78 : 72;
    }
    if (dhwDeliveryMethod === 'unknown') {
      // Avoid hard gravity assumptions when delivery method is not known.
      return isMixergy ? 60 : 58;
    }
    // gravity
    return isMixergy ? 65 : 55;
  }
  
  if (isCombi) {
    const flow = survey.mainsFlowLpm || survey.flowRate || 0;
    const occupancy = survey.occupancy ?? 0;
    const draws = survey.drawsPerDay ?? 0;
    let score = 30;
    if (flow >= 12) {
      score = 75;
    } else if (flow > 0) {
      score = 50;
    }

    if (isHighDhwDemandContext(survey)) {
      score = Math.min(score - 15, 65);
    }

    // Concurrency penalty for larger households / frequent draws.
    // On-demand DHW can still be viable, but should rank lower for peak simultaneous use.
    if (occupancy >= 4 || draws >= 7) {
      score -= 10;
    }
    if (occupancy >= 5 || draws >= 9) {
      score -= 10;
    }

    return Math.max(20, score);
  }
  return 60;
}

function scoreTechEmitterCompat(option: RecommendationOption, survey: SystemRecInput): number {
  if (!isHeatPumpModeOption(option)) return 85; // conventional temps — most emitters fine

  const lowTempSuitability = survey.emitters?.lowTempSuitability;
  if (lowTempSuitability === 'likely_ok') return 95;
  if (lowTempSuitability === 'unknown' || !lowTempSuitability) return 45;
  if (lowTempSuitability === 'definitely_needs_upgrade') return 20;
  if (lowTempSuitability === 'likely_needs_upgrade') return 30;
  return 50;
}

function scoreTechHeatLossCompat(option: RecommendationOption, survey: SystemRecInput): number {
  // Prefer canonical heatDemand fields, then legacy fields
  const designKw =
    survey.heatDemand?.designHeatLossKw ||
    survey.knownHeatLossKw ||
    survey.heatLossKw ||
    0;

  // If annual space heat demand is known, derive an equivalent design kW
  // using inverse of degree-day model: 1900 HDD * 24h / 24 K deltaT ≈ 1900 FLH
  const annualKwh = survey.heatDemand?.annualSpaceHeatDemandKwh;
  const effectiveKw = designKw > 0
    ? designKw
    : annualKwh && annualKwh > 0
      ? annualKwh / 1900  // approx full-load hours
      : 0;

  if (!effectiveKw) return 50; // unknown — debug: heat_loss_compat using fallback=50

  // debug note: heat_loss_compat using known heatDemand field
  // For heat pumps, lower heat loss = better COP
  if (effectiveKw <= 8) return 90;
  if (effectiveKw <= 12) return 75;
  if (effectiveKw <= 16) return 55;
  return 25; // very high heat loss — fabric first
}

function scoreTechStandingLoss(option: RecommendationOption, survey: SystemRecInput): number {
  const isCombi = option.systemType === 'combi';
  if (isCombi) return 90; // no cylinder = no standing loss

  const isMixergy = option.waterLabel?.toLowerCase().includes('mixergy') || option.title?.toLowerCase().includes('mixergy');
  const isUnvented = option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented');
  const isVented = option.water === 'open_vented' || option.waterLabel?.toLowerCase().includes('open vented');

  const hasNumericStandingLoss =
    (typeof survey.currentStandingLossKwhPerDay === 'number' && Number.isFinite(survey.currentStandingLossKwhPerDay)) ||
    (typeof survey.proposedStandingLossKwhPerDay === 'number' && Number.isFinite(survey.proposedStandingLossKwhPerDay));

  // Backward-compatible behavior when no numeric standing-loss data is provided.
  if (!hasNumericStandingLoss) {
    if (isMixergy && isUnvented) return 95;
    if (isMixergy && isVented) return 85;
    if (isUnvented) return 60;
    if (isVented) return 50;
    return 60;
  }

  const currentStandingLoss = typeof survey.currentStandingLossKwhPerDay === 'number' && Number.isFinite(survey.currentStandingLossKwhPerDay)
    ? survey.currentStandingLossKwhPerDay
    : 1.5;

  // Assume recommended works are done when scoring option standing losses.
  let proposedStandingLoss = typeof survey.proposedStandingLossKwhPerDay === 'number' && Number.isFinite(survey.proposedStandingLossKwhPerDay)
    ? survey.proposedStandingLossKwhPerDay
    : currentStandingLoss;

  if (survey.cylinderSpace !== false) {
    if (isMixergy) {
      proposedStandingLoss = 0.6;
    } else if (proposedStandingLoss > 0.8) {
      proposedStandingLoss = 0.8;
    }
  }

  if (proposedStandingLoss <= 0.6) return 95;
  if (proposedStandingLoss <= 0.8) return 90;
  if (proposedStandingLoss <= 1.2) return 75;
  if (proposedStandingLoss <= 1.6) return 60;
  if (proposedStandingLoss <= 2.2) return 45;
  return 35;
}

/**
 * Apply conditional scoring adjustments based on load thresholds
 * This implements context-aware engineering logic where scoring adapts to system load
 * 
 * Returns an object with:
 * - scoreAdjustment: the total score adjustment to apply
 * - explanations: array of explanation strings for the UI
 */
function applyConditionalLoadAdjustments(
  option: RecommendationOption,
  survey: SystemRecInput
): { scoreAdjustment: number; explanations: string[] } {
  let scoreAdjustment = 0;
  const explanations: string[] = [];
  const designLoadKw = getDesignLoadKw(survey);
  
  if (!designLoadKw) {
    // No load data available - return neutral
    return { scoreAdjustment: 0, explanations: [] };
  }

  // Load-based zoning logic: S-Plan advantage above 18 kW
  const isSPlan = isSPlanDistribution(option, survey);
  const isYPlan = isYPlanDistribution(option, survey);
  
  if (designLoadKw > LOAD_ZONING_THRESHOLD_KW) {
    if (isSPlan) {
      scoreAdjustment += CONDITIONAL_SCORE_ADJUSTMENTS.ZONING_BONUS_ABOVE_THRESHOLD;
      explanations.push(THRESHOLD_EXPLANATIONS.ZONING_ABOVE_THRESHOLD(designLoadKw));
    }
    // Note: Y-Plan doesn't get penalized, just doesn't get the bonus
  } else {
    // Below threshold: both Y-Plan and S-Plan are neutral (no adjustment)
    if (isSPlan || isYPlan) {
      explanations.push(THRESHOLD_EXPLANATIONS.ZONING_BELOW_THRESHOLD(designLoadKw));
    }
  }

  // Primary size conditional logic: 28mm advantage/disadvantage based on load
  const primarySize = getPrimaryPipeSize(survey);
  const isUnvented = isUnventedArchitecture(option);
  const includesSPlan = isSPlanDistribution(option, survey);

  if (primarySize === 28) {
    if (designLoadKw >= LOAD_PRIMARY_THRESHOLD_KW) {
      scoreAdjustment += CONDITIONAL_SCORE_ADJUSTMENTS.PRIMARY_28MM_BONUS_ABOVE_THRESHOLD;
      explanations.push(THRESHOLD_EXPLANATIONS.PRIMARY_28MM_ADVANTAGE(designLoadKw));
    } else {
      scoreAdjustment += CONDITIONAL_SCORE_ADJUSTMENTS.PRIMARY_28MM_PENALTY_BELOW_THRESHOLD;
      explanations.push(THRESHOLD_EXPLANATIONS.PRIMARY_28MM_OVERSIZED(designLoadKw));
    }
  }

  if (designLoadKw > LOAD_PRIMARY_THRESHOLD_KW && primarySize !== null && primarySize <= 22 && !includesSPlan) {
    explanations.push(
      `Heat demand ${designLoadKw} kW – recommend 28mm primaries and S-plan zoning unless already present or included.`
    );
  }

  if (isUnvented && !includesSPlan) {
    explanations.push('Unvented cylinder conversion should include S-plan zoning change.');
  }

  return { scoreAdjustment, explanations };
}


// ============================================================================
// Priority Scoring Functions
// ============================================================================

function scoreLowUpfrontCost(option: RecommendationOption, survey: SystemRecInput): number {
  // Like-for-like boiler replacement = high score
  // Heat pump = low score (unless heavily subsidized)
  if (option.systemType === 'combi' || option.systemType === 'system' || option.systemType === 'regular') {
    let score = 90;

    if (isArchitectureChange(option, survey)) {
      score -= 20;
    }

    if (isStoredToOnDemandConversion(option, survey)) {
      score -= 10;

      if (survey.sludgeRisk === 'CONFIRMED' || survey.systemAlreadySealed === 'no') {
        score -= 10;
      }
    }

    return Math.max(30, score);
  }
  if (option.systemType === 'heat_pump') {
    return 30;
  }
  if (option.systemType === 'hybrid') {
    return 50;
  }
  return 50;
}

function scoreLowRunningCost(option: RecommendationOption, survey: SystemRecInput): number | null {
  const hasProvidedAnnualDemand = typeof survey.actualAnnualHeatKwh === 'number' && Number.isFinite(survey.actualAnnualHeatKwh);
  const allowAssumedDemand = survey.costPriority === 'ongoing_cost';

  // Default policy: do not fabricate running-cost scores without annual demand evidence.
  // Optional deterministic assumption path: when user explicitly prioritizes ongoing cost,
  // allow an assumed demand estimate with caveat in confidence adjustments.
  if (!hasProvidedAnnualDemand && !allowAssumedDemand) {
    return null;
  }

  // Heat pump (when viable) = high score
  // Modern boiler with optimization = good score
  // Hybrid = variable
  if (isHeatPumpModeOption(option)) {
    const lowTempSuitability = survey.emitters?.lowTempSuitability;
    if (lowTempSuitability === 'likely_ok' || lowTempSuitability === 'unknown') {
      return 95;
    }
    return 70; // Lower if emitters need upgrade
  }
  if (option.systemType === 'hybrid') {
    return 75;
  }
  // Modern condensing boiler
  if (option.boilerLabel?.toLowerCase().includes('condensing') || option.boilerLabel?.toLowerCase().includes('a-rated')) {
    return 70;
  }
  return 60;
}

function scoreReliabilitySimplicity(option: RecommendationOption, survey: SystemRecInput): number {
  // Simpler systems score higher
  // Combi = high (fewer components)
  // Heat pump = lower (more complex)
  if (option.systemType === 'combi') {
    return 90;
  }
  if (option.systemType === 'system' || option.systemType === 'regular') {
    return 80;
  }
  if (option.systemType === 'heat_pump') {
    return 60;
  }
  if (option.systemType === 'hybrid') {
    return 55;
  }
  return 70;
}

function scoreHotWaterPerformance(option: RecommendationOption, survey: SystemRecInput): number {
  // Unvented cylinder with good mains = high
  // Combi with poor mains = low
  const hasGoodMains = (survey.mainsFlowLpm || survey.flowRate || 0) > 15 && 
                       (survey.mainsStaticPressureBar || survey.mainsPressure || 0) > 1.5;
  
  if (option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented')) {
    return hasGoodMains ? 95 : 70;
  }
  if (option.systemType === 'combi') {
    return hasGoodMains ? 80 : 50;
  }
  if (option.water === 'open_vented' || option.waterLabel?.toLowerCase().includes('vented')) {
    return 85;
  }
  return 70;
}

function scoreQuietOperation(option: RecommendationOption, survey: SystemRecInput): number {
  // Internal boiler = quietest
  // Heat pump outdoor unit = noisier
  if (option.systemType === 'heat_pump') {
    return 50; // Outdoor unit noise
  }
  if (option.systemType === 'combi' || option.systemType === 'system' || option.systemType === 'regular') {
    return 85; // Internal boiler, generally quiet
  }
  return 70;
}

function scoreFutureProofing(option: RecommendationOption, survey: SystemRecInput): number {
  // Heat pump ready = high
  // Low-temp ready pipework = good
  // Modern controls = good
  if (option.systemType === 'heat_pump') {
    return 95;
  }
  if (option.systemType === 'hybrid') {
    return 90;
  }
  // System with good controls
  if (option.thermostat?.supportsLoadCompensation) {
    return 75;
  }
  return 60;
}

function scoreMinimalDisruption(option: RecommendationOption, survey: SystemRecInput): number {
  const curBoiler = survey.currentBoiler || '';
  const curWater = survey.currentWater || '';
  const optBoiler = option.boiler || option.systemType || '';
  const optWater = option.water || '';

  // Like-for-like: same boiler family AND same water type
  if (curBoiler && curWater && optBoiler === curBoiler && optWater === curWater) {
    return 95;
  }
  // Same boiler family, different water type
  if (curBoiler && optBoiler === curBoiler) {
    return 82;
  }
  // Boiler-to-boiler swap (regular ↔ system ↔ combi)
  const boilerFamilies = ['regular', 'system', 'combi'];
  if (boilerFamilies.includes(optBoiler) && boilerFamilies.includes(curBoiler)) {
    return 60;
  }
  // Heat pump — major change
  if (isHeatPumpModeOption(option)) {
    const lowTempSuitability = survey.emitters?.lowTempSuitability;
    if (lowTempSuitability === 'likely_ok') {
      return 40;
    }
    return 25; // Needs emitter upgrades too
  }
  // Fallback — title-based for legacy options
  if (option.title?.toLowerCase().includes('like-for-like') ||
      option.title?.toLowerCase().includes('replacement')) {
    return 95;
  }
  // No current system info — neutral
  return curBoiler ? 55 : 65;
}

function scoreSmartControls(option: RecommendationOption, survey: SystemRecInput): number {
  // Options with advanced controls score higher
  if (option.thermostat?.supportsLoadCompensation || option.thermostat?.supportsSmartStart) {
    return 90;
  }
  if (option.systemType === 'heat_pump') {
    return 85; // Heat pumps typically include smart controls
  }
  if (option.includesSmartControl) {
    return 80;
  }
  return 50;
}

function scoreEnvironmentalImpact(option: RecommendationOption, survey: SystemRecInput): number {
  // Heat pump = best
  // Hybrid = good
  // Gas boiler = lower
  if (option.systemType === 'heat_pump') {
    return 95;
  }
  if (option.systemType === 'hybrid') {
    return 80;
  }
  // Modern condensing boiler
  if (option.boilerLabel?.toLowerCase().includes('condensing')) {
    return 60;
  }
  return 50;
}

function scoreComfortEvenHeat(option: RecommendationOption, survey: SystemRecInput): number {
  // UFH = best
  // Heat pump with good emitters = good
  // System with zoning = good
  if (survey.heatEmitterType === 'underfloor_heating') {
    return 95;
  }
  if (option.systemType === 'heat_pump') {
    return 85; // Lower flow temps = more even heat
  }
  if (option.thermostat?.protocol === 'OpenTherm' || option.thermostat?.protocol === 'eBUS') {
    return 80; // Modulating control
  }
  return 70;
}

// ============================================================================
// Future Plan Scoring Functions
// ============================================================================

function scoreCapacityGrowth(option: RecommendationOption, survey: SystemRecInput): number {
  // Systems with headroom for expansion score higher
  // Heat pumps with modular capacity
  // Oversized boilers
  if (option.systemType === 'heat_pump') {
    return 85; // Easier to add zones
  }
  if (option.systemType === 'system' || option.systemType === 'regular') {
    return 80; // Good for zoning
  }
  if (option.systemType === 'combi') {
    return 60; // Limited zoning capability
  }
  return 70;
}

function scoreDHWGrowth(option: RecommendationOption, survey: SystemRecInput): number {
  // Cylinder-based systems score higher
  // Larger cylinder options
  if (option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented')) {
    return 90; // Easy to upgrade cylinder size
  }
  if (option.water === 'open_vented' || option.waterLabel?.toLowerCase().includes('vented')) {
    return 85;
  }
  if (option.systemType === 'combi') {
    return 40; // Limited by flow rate
  }
  return 70;
}

function scoreUFHFuture(option: RecommendationOption, survey: SystemRecInput): number {
  // Low-temp capable systems score higher
  if (option.systemType === 'heat_pump') {
    return 95; // Perfect for UFH
  }
  if (option.systemType === 'hybrid') {
    return 85;
  }
  // Modern condensing boiler with weather comp
  if (option.thermostat?.supportsLoadCompensation) {
    return 75;
  }
  return 60;
}

function scoreElectrification(option: RecommendationOption, survey: SystemRecInput): number {
  // Heat pump and electric systems score higher
  // Load shifting capability
  if (option.systemType === 'heat_pump') {
    return 95; // Synergy with solar/battery
  }
  if (option.systemType === 'hybrid') {
    return 85;
  }
  // Electric storage
  if (option.water?.includes('heat_battery')) {
    return 80;
  }
  return 50;
}

// ============================================================================
// Kill Switch Detection and Penalty Computation
// ============================================================================

/**
 * Detect fatal flaws (kill switches) that make a system unsuitable
 * These are hard constraints that justify a 0 or near-0 score
 */
function detectKillSwitches(
  option: RecommendationOption,
  survey: SystemRecInput,
  technicalFit: number,
  feasibility: number
): string[] {
  const killSwitches: string[] = [];

  // Combi with insufficient DHW capacity for household size
  if (option.systemType === 'combi') {
    const occupants = survey.occupancy || 0;
    const bathrooms = survey.bathrooms || 0;
    const flow = survey.mainsFlowLpm || survey.flowRate || 0;
    const hasFlowMeasurement = flow > 0;
    
    // Fatal: Combi with >3 occupants or >2 bathrooms AND confirmed low flow
    // Only trigger if we have a measurement showing inadequate flow
    if ((occupants > 3 || bathrooms > 2) && hasFlowMeasurement && flow < 12) {
      killSwitches.push(
        `Incompatible: Combi cannot provide adequate hot water for simultaneous tap use in a household with ${occupants} occupants and ${bathrooms} bathrooms`
      );
    }
    
    // Fatal: Multiple simultaneous showers requirement with confirmed insufficient flow
    if (bathrooms >= 3 && hasFlowMeasurement && flow < 15) {
      killSwitches.push('Incompatible: Insufficient flow rate for 3+ bathrooms with simultaneous use');
    }
  }

  // Heat pump with incompatible emitters
  if (isHeatPumpModeOption(option)) {
    const lowTempSuitability = survey.emitters?.lowTempSuitability;
    if (lowTempSuitability === 'definitely_needs_upgrade' && feasibility < 0.5) {
      killSwitches.push('Incompatible: Emitters require major upgrade for low-temperature heat pump operation');
    }
  }

  // Mains-dependent systems without adequate supply
  const isUnvented = option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented');
  const isCombi = option.systemType === 'combi';
  if (isUnvented || isCombi) {
    // Parse values properly - don't coerce null/undefined/empty to 0
    const flowRaw = survey.mainsFlowLpm ?? survey.flowRate;
    const pressureRaw = survey.mainsStaticPressureBar ?? survey.mainsPressure;
    
    // Convert to number | null (handle string values from legacy data)
    let flow: number | null = null;
    if (typeof flowRaw === 'number' && isFinite(flowRaw)) {
      flow = flowRaw;
    } else if (typeof flowRaw === 'string') {
      const trimmed = (flowRaw as string).trim(); // Type assertion needed due to TS narrowing
      if (trimmed !== '') {
        flow = parseFloat(trimmed);
      }
    }
    
    let pressure: number | null = null;
    if (typeof pressureRaw === 'number' && isFinite(pressureRaw)) {
      pressure = pressureRaw;
    } else if (typeof pressureRaw === 'string') {
      const trimmed = (pressureRaw as string).trim(); // Type assertion needed due to TS narrowing
      if (trimmed !== '') {
        pressure = parseFloat(trimmed);
      }
    }
    
    const hasFlowMeasurement = flow !== null && isFinite(flow);
    const hasPressureMeasurement = pressure !== null && isFinite(pressure);
    
    // Only trigger kill switch if we have BOTH measurements AND they show inadequacy
    // Missing values (null) should NOT trigger hard fail - they reduce confidence instead
    // hasFlowMeasurement and hasPressureMeasurement already guarantee non-null values
    if (isUnvented && hasFlowMeasurement && hasPressureMeasurement) {
      if (flow! <= 12 || pressure! <= 1.0) {
        killSwitches.push('Incompatible: Mains supply insufficient for unvented cylinder (needs >12 lpm, >1.0 bar)');
      }
    }
    if (isCombi && hasFlowMeasurement && hasPressureMeasurement) {
      // Fixed threshold: Only fail when pressure is CONFIRMED LOW (<= 1.5 bar for combi)
      // Good pressure (e.g., 2.5 bar) should NOT trigger this warning
      if (flow! <= 14 || pressure! <= 1.5) {
        killSwitches.push('Incompatible: Mains supply insufficient for combi boiler performance (needs >14 lpm, >1.5 bar)');
      }
    }
  }

  // Gas system without gas availability
  if ((option.systemType === 'combi' || option.systemType === 'system' || option.systemType === 'regular') 
      && survey.gasAvailable === false) {
    killSwitches.push('Incompatible: Gas system but no gas supply available');
  }

  // Note: Removed generic "Multiple technical constraints" message (line 1003)
  // Instead, specific constraints are now properly identified and classified by domain
  // in buildConstraintReasons() to avoid mislabeling preference-based failures as technical

  return killSwitches;
}

/**
 * Compute explicit penalty breakdown for transparency
 * Shows what's dragging down the score
 */
function computePenalties(
  option: RecommendationOption,
  survey: SystemRecInput,
  feasibility: number
): Array<{ dimension: string; penalty: number; reason: string }> {
  const penalties: Array<{ dimension: string; penalty: number; reason: string }> = [];

  // Only show penalties when feasibility is significantly reduced OR there are known issues
  // This helps explain why scores are lower even if feasibility calculation might not catch everything
  const showPenalties = feasibility < 0.8 || (option.score !== undefined && option.score < 50);
  
  if (!showPenalties) return penalties;

  // Missing critical inputs penalty
  if (option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented')) {
    if (!survey.mainsFlowLpm && !survey.flowRate) {
      penalties.push({
        dimension: 'DHW Capacity',
        penalty: -20,
        reason: 'Mains flow not measured - unvented performance uncertain'
      });
    }
    if (!survey.mainsStaticPressureBar && !survey.mainsPressure) {
      penalties.push({
        dimension: 'DHW Delivery',
        penalty: -20,
        reason: 'Mains pressure not measured - delivery quality uncertain'
      });
    }
  }

  // Emitter incompatibility penalty
  if (isHeatPumpModeOption(option)) {
    const lowTempSuitability = survey.emitters?.lowTempSuitability;
    if (lowTempSuitability === 'definitely_needs_upgrade') {
      penalties.push({
        dimension: 'Emitter Compatibility',
        penalty: -30,
        reason: 'Emitters definitely need upgrade for low-temperature operation'
      });
    } else if (lowTempSuitability === 'likely_needs_upgrade') {
      penalties.push({
        dimension: 'Emitter Compatibility',
        penalty: -15,
        reason: 'Emitters likely need upgrade for optimal performance'
      });
    }
  }

  // Gas availability penalty
  if ((option.systemType === 'combi' || option.systemType === 'system' || option.systemType === 'regular') 
      && survey.gasAvailable === false) {
    penalties.push({
      dimension: 'Fuel Availability',
      penalty: -50,
      reason: 'No gas supply available to property'
    });
  }

  // Combi DHW capacity penalty
  if (option.systemType === 'combi') {
    const occupants = survey.occupancy || 0;
    const bathrooms = survey.bathrooms || 0;
    if ((occupants > 3 || bathrooms > 2)) {
      const flow = survey.mainsFlowLpm || survey.flowRate || 0;
      if (flow < 12) {
        penalties.push({
          dimension: 'DHW Capacity',
          penalty: -40,
          reason: `Insufficient for ${occupants} occupants and ${bathrooms} bathrooms - simultaneous use limited`
        });
      } else if (flow < 15) {
        penalties.push({
          dimension: 'DHW Capacity',
          penalty: -20,
          reason: 'Marginal for household size - may struggle with peak demand'
        });
      }
    }
  }

  return penalties;
}

// ============================================================================
// Confidence Adjustments
// ============================================================================

function computeConfidenceAdjustments(
  option: RecommendationOption,
  priorities: UpgradePriority[],
  survey: SystemRecInput
): string[] {
  const adjustments: string[] = [];

  // Check if hot water performance is a priority but mains not measured
  const hotWaterPriority = priorities.find(p => p.key === 'hot_water_performance');
  if (hotWaterPriority && hotWaterPriority.rank <= 2) {
    if (!survey.mainsFlowLpm && !survey.flowRate) {
      adjustments.push('Hot water performance prioritized but mains flow not measured');
    }
    if (!survey.mainsStaticPressureBar && !survey.mainsPressure) {
      adjustments.push('Hot water performance prioritized but mains pressure not measured');
    }
  }

  // Check if heat pump option but heat loss unknown
  if (option.systemType === 'heat_pump') {
    const hasHeatLoss =
      survey.heatLossKw ||
      survey.knownHeatLossKw ||
      survey.heatLossRooms?.length ||
      survey.heatDemand?.designHeatLossKw ||
      survey.heatDemand?.annualSpaceHeatDemandKwh;
    if (!hasHeatLoss) {
      adjustments.push('Heat pump sizing requires heat loss calculation');
    }
  }

  // Check for future plans requiring unknown capacity — provide quantified context when available
  const capacityPlans = ['loft_conversion', 'rear_extension', 'side_extension', 'garage_conversion'];
  const hasCapacityPlan = survey.futurePlans?.some(p => capacityPlans.includes(p.key));
  if (hasCapacityPlan) {
    const roomBasedHeatLoss = survey.heatLossRooms?.length
      ? calculateWholeHouseHeatLoss(
          survey.heatLossRooms,
          survey.heatLossDesignTempInternalC ?? 21,
          survey.heatLossDesignTempExternalC ?? -3
        ).totalHeatLossKW
      : null;
    const knownHeatLoss = roomBasedHeatLoss || survey.heatDemand?.designHeatLossKw || survey.knownHeatLossKw || survey.heatLossKw;

    if (!knownHeatLoss) {
      adjustments.push('Final appliance output to be confirmed during detailed heat loss validation prior to installation.');
    } else {
      // Estimate headroom: typical boiler is sized at round-up to 5 kW increment with 20% DHW headroom
      const nominalKw = Math.max(Math.ceil(knownHeatLoss / 5) * 5, knownHeatLoss * 1.2);
      const headroomPct = Math.round(((nominalKw - knownHeatLoss) / knownHeatLoss) * 100);
      adjustments.push(`Design heat loss: ${knownHeatLoss.toFixed(1)} kW — selected system provides ${headroomPct}% headroom.`);
    }
  }

  // Running-cost caveat when using assumed demand path
  const runningCostPrioritized = priorities.some((p) => p.key === 'low_running_cost');
  const hasProvidedAnnualDemand = typeof survey.actualAnnualHeatKwh === 'number' && Number.isFinite(survey.actualAnnualHeatKwh);
  if (runningCostPrioritized && !hasProvidedAnnualDemand && survey.costPriority === 'ongoing_cost') {
    adjustments.push('Low running cost estimated using assumed annual demand (no provided bill/meter demand)');
  }

  return adjustments;
}

// ============================================================================
// Reason Generation
// ============================================================================

function generateReasons(
  option: RecommendationOption,
  priorities: UpgradePriority[],
  futurePlans: FuturePlan[],
  survey: SystemRecInput,
  conditionalExplanations: string[] = []
): string[] {
  const reasons: string[] = [];

  // Add conditional logic explanations first (these are engineering-based)
  reasons.push(...conditionalExplanations);

  // Architecture-specific explanations
  if (isUnventedArchitecture(option)) {
    reasons.push(THRESHOLD_EXPLANATIONS.ARCHITECTURE_UNVENTED_UPGRADE());
  }

  // Mixergy-specific explanation (as cylinder modifier, not system type)
  const isMixergy = option.waterLabel?.toLowerCase().includes('mixergy') || 
                    option.title?.toLowerCase().includes('mixergy');
  if (isMixergy) {
    reasons.push(THRESHOLD_EXPLANATIONS.CYLINDER_MIXERGY_BENEFIT());
  }

  // Add priority-based reasons (top 2 priorities only)
  const topPriorities = priorities.slice(0, 2);
  for (const priority of topPriorities) {
    const reason = getPriorityReason(option, priority.key, survey);
    if (reason) {
      reasons.push(reason);
    }
  }

  // Add future plan reasons (if any)
  const relevantPlans = futurePlans.filter(p => p.key !== 'no_plans' && p.key !== 'unsure');
  if (relevantPlans.length > 0) {
    const planReason = getFuturePlanReason(option, relevantPlans, survey);
    if (planReason) {
      reasons.push(planReason);
    }
  }

  // Deduplicate and return
  return Array.from(new Set(reasons));
}

function getPriorityReason(option: RecommendationOption, priorityKey: PriorityKey, survey: SystemRecInput): string | null {
  switch (priorityKey) {
    case 'low_upfront_cost':
      if (isArchitectureChange(option, survey)) {
        return 'Upfront costs include architecture-change work (pipework, controls, and commissioning), so this is not a like-for-like swap';
      }
      if (isStoredToOnDemandConversion(option, survey)) {
        return 'Upfront costs reflect conversion scope from stored to on-demand hot water, not a like-for-like swap';
      }
      if (option.systemType === 'combi' || option.systemType === 'system') {
        return 'Keeps upfront costs manageable with like-for-like approach';
      }
      return null;
    
    case 'low_running_cost':
      if (option.systemType === 'heat_pump') {
        return 'Minimizes running costs with high-efficiency heat pump';
      }
      return null;
    
    case 'hot_water_performance':
      if (option.water === 'unvented' || option.waterLabel?.toLowerCase().includes('unvented')) {
        return 'Delivers excellent hot water performance with unvented cylinder';
      }
      return null;
    
    case 'future_proofing':
      if (option.systemType === 'heat_pump') {
        return 'Future-proofed for net zero transition';
      }
      return null;
    
    case 'minimal_disruption':
      if (option.title?.toLowerCase().includes('like-for-like')) {
        return 'Minimal disruption with straightforward replacement';
      }
      return null;
    
    case 'environmental_impact':
      if (option.systemType === 'heat_pump') {
        return 'Lowest carbon footprint option';
      }
      return null;
    
    default:
      return null;
  }
}

function getFuturePlanReason(option: RecommendationOption, plans: FuturePlan[], survey: SystemRecInput): string | null {
  const planKeys = plans.map(p => p.key);
  
  // Check for capacity expansion plans
  const capacityPlans = planKeys.filter(k => 
    ['loft_conversion', 'rear_extension', 'side_extension', 'garage_conversion'].includes(k)
  );
  if (capacityPlans.length > 0 && (option.systemType === 'system' || option.systemType === 'heat_pump')) {
    return 'Designed with headroom for planned property extensions';
  }
  
  // Check for electrification plans
  const electrificationPlans = planKeys.filter(k => 
    ['solar_pv', 'battery_storage', 'ev_charger'].includes(k)
  );
  if (electrificationPlans.length > 0 && option.systemType === 'heat_pump') {
    return 'Integrates with planned solar/battery/EV infrastructure';
  }
  
  // Check for DHW growth
  if (planKeys.includes('additional_bathroom') && option.water?.includes('unvented')) {
    return 'Cylinder-based design ready for additional bathroom';
  }
  
  return null;
}
